// Multi-Cart UI Controller with Share & Import Features
class MultiCartUI {
  constructor() {
    this.manager = window.multiCartManager;
    this.panel = document.getElementById('multiCartPanel');
    this.toggle = document.getElementById('multiCartToggle');
    this.cartList = document.getElementById('cartList');
    this.createBtn = document.getElementById('createCartBtn');
    this.importBtn = document.getElementById('importCartBtn');
    this.modal = document.getElementById('modalOverlay');
    this.modalTitle = document.getElementById('modalTitle');
    this.modalInput = document.getElementById('cartNameInput');
    this.modalConfirm = document.getElementById('modalConfirm');
    this.modalCancel = document.getElementById('modalCancel');
    this.cartCount = document.getElementById('cartCount');
    
    // Share modal elements
    this.shareModal = document.getElementById('shareModalOverlay');
    this.shareLink = document.getElementById('shareLinkInput');
    this.copyLinkBtn = document.getElementById('copyLinkBtn');
    this.shareModalClose = document.getElementById('shareModalClose');
    
    // Import modal elements
    this.importModal = document.getElementById('importModalOverlay');
    this.importTextarea = document.getElementById('importDataInput');
    this.importConfirmBtn = document.getElementById('importConfirmBtn');
    this.importModalCancel = document.getElementById('importModalCancel');
    
    this.currentAction = null;
    this.currentCartId = null;
    
    this.init();
  }

  init() {
    // Event listeners
    this.toggle.addEventListener('click', () => this.togglePanel());
    this.createBtn.addEventListener('click', () => this.showCreateModal());
    this.importBtn.addEventListener('click', () => this.showImportModal());
    this.modalCancel.addEventListener('click', () => this.hideModal());
    this.modalConfirm.addEventListener('click', () => this.handleModalConfirm());
    
    // Share modal listeners
    this.shareModalClose.addEventListener('click', () => this.hideShareModal());
    this.copyLinkBtn.addEventListener('click', () => this.copyShareLink());
    
    // Import modal listeners
    this.importModalCancel.addEventListener('click', () => this.hideImportModal());
    this.importConfirmBtn.addEventListener('click', () => this.handleImportConfirm());
    
    // Close panel when clicking outside
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.multi-cart-widget')) {
        this.panel.classList.remove('active');
      }
    });

    // Render carts
    this.renderCarts();
    this.updateCartCount();
  }

  togglePanel() {
    this.panel.classList.toggle('active');
    this.renderCarts();
  }

  renderCarts() {
    const carts = this.manager.getAllCarts();
    const currentCartId = this.manager.getCurrentCartId();
    
    if (Object.keys(carts).length === 0) {
      this.cartList.innerHTML = `
        <div class="empty-state">
          <div class="empty-state-icon">🛒</div>
          <p>No carts yet. Create your first cart!</p>
        </div>
      `;
      return;
    }

    this.cartList.innerHTML = Object.values(carts).map(cart => {
      const isActive = cart.id === currentCartId;
      const itemCount = this.manager.getCartItemCount(cart.id);
      const createdDate = new Date(cart.createdAt).toLocaleDateString();
      
      return `
        <div class="cart-item ${isActive ? 'active' : ''}" data-cart-id="${cart.id}">
          <div class="cart-item-header">
            <div class="cart-item-name">${cart.name}</div>
          </div>
          <div class="cart-item-info">
            <span>📦 ${itemCount} items</span>
            <span>📅 ${createdDate}</span>
          </div>
          <div class="cart-item-actions">
            ${!isActive ? `
              <button class="cart-action-btn switch-btn" data-cart-id="${cart.id}">
                Switch
              </button>
            ` : ''}
            <button class="cart-action-btn share-btn" data-cart-id="${cart.id}">
              🔗 Share
            </button>
            ${!isActive ? `
              <button class="cart-action-btn rename-btn" data-cart-id="${cart.id}">
                Rename
              </button>
              <button class="cart-action-btn danger delete-btn" data-cart-id="${cart.id}">
                Delete
              </button>
            ` : `
              <button class="cart-action-btn rename-btn" data-cart-id="${cart.id}">
                Rename
              </button>
            `}
          </div>
        </div>
      `;
    }).join('');

    this.attachCartActionListeners();
  }

  attachCartActionListeners() {
    // Switch cart
    document.querySelectorAll('.switch-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const cartId = btn.dataset.cartId;
        this.switchCart(cartId);
      });
    });

    // Share cart
    document.querySelectorAll('.share-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const cartId = btn.dataset.cartId;
        this.showShareModal(cartId);
      });
    });

    // Rename cart
    document.querySelectorAll('.rename-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const cartId = btn.dataset.cartId;
        this.showRenameModal(cartId);
      });
    });

    // Delete cart
    document.querySelectorAll('.delete-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const cartId = btn.dataset.cartId;
        this.deleteCart(cartId);
      });
    });
  }

  async switchCart(cartId) {
    const cart = this.manager.getCart(cartId);
    if (!cart) return;

    if (confirm(`Switch to "${cart.name}"? Your current cart will be saved.`)) {
      await this.manager.switchCart(cartId);
    }
  }

  showShareModal(cartId) {
    const cart = this.manager.getCart(cartId);
    if (!cart) return;

    const shareUrl = this.manager.generateShareLink(cartId);
    this.shareLink.value = shareUrl;
    this.currentCartId = cartId;
    this.shareModal.classList.add('active');
    this.shareLink.select();
  }

  hideShareModal() {
    this.shareModal.classList.remove('active');
    this.currentCartId = null;
  }

  async copyShareLink() {
    try {
      await navigator.clipboard.writeText(this.shareLink.value);
      
      // Change button text temporarily
      const originalText = this.copyLinkBtn.textContent;
      this.copyLinkBtn.textContent = '✓ Copied!';
      this.copyLinkBtn.style.background = '#27ae60';
      
      setTimeout(() => {
        this.copyLinkBtn.textContent = originalText;
        this.copyLinkBtn.style.background = '';
      }, 2000);
    } catch (error) {
      alert('Please manually copy the link above');
    }
  }

  showImportModal() {
    this.importTextarea.value = '';
    this.importModal.classList.add('active');
    this.importTextarea.focus();
  }

  hideImportModal() {
    this.importModal.classList.remove('active');
  }

  async handleImportConfirm() {
    const data = this.importTextarea.value.trim();
    if (!data) {
      alert('Please paste cart data');
      return;
    }

    const cartId = await this.manager.importCartData(data);
    if (cartId) {
      alert('Cart imported successfully!');
      this.hideImportModal();
      this.renderCarts();
      this.updateCartCount();
    } else {
      alert('Invalid cart data. Please check and try again.');
    }
  }

  showCreateModal() {
    this.currentAction = 'create';
    this.modalTitle.textContent = 'Create New Cart';
    this.modalInput.value = '';
    this.modalInput.placeholder = 'Enter cart name...';
    this.modalConfirm.textContent = 'Create';
    this.modal.classList.add('active');
    this.modalInput.focus();
  }

  showRenameModal(cartId) {
    const cart = this.manager.getCart(cartId);
    if (!cart) return;

    this.currentAction = 'rename';
    this.currentCartId = cartId;
    this.modalTitle.textContent = 'Rename Cart';
    this.modalInput.value = cart.name;
    this.modalInput.placeholder = 'Enter new name...';
    this.modalConfirm.textContent = 'Rename';
    this.modal.classList.add('active');
    this.modalInput.focus();
    this.modalInput.select();
  }

  hideModal() {
    this.modal.classList.remove('active');
    this.currentAction = null;
    this.currentCartId = null;
  }

  async handleModalConfirm() {
    const name = this.modalInput.value.trim();
    if (!name) {
      alert('Please enter a cart name');
      return;
    }

    if (this.currentAction === 'create') {
      await this.manager.createCart(name);
    } else if (this.currentAction === 'rename') {
      this.manager.renameCart(this.currentCartId, name);
    }

    this.hideModal();
    this.renderCarts();
    this.updateCartCount();
  }

  async deleteCart(cartId) {
    const cart = this.manager.getCart(cartId);
    if (!cart) return;

    if (confirm(`Are you sure you want to delete "${cart.name}"?`)) {
      await this.manager.deleteCart(cartId);
      this.renderCarts();
      this.updateCartCount();
    }
  }

  updateCartCount() {
    const carts = this.manager.getAllCarts();
    this.cartCount.textContent = Object.keys(carts).length;
  }
}

// Initialize UI when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.multiCartUI = new MultiCartUI();
  });
} else {
  window.multiCartUI = new MultiCartUI();
}